/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-use-before-define */
import axios, { AxiosRequestConfig } from 'axios';
import { navigateTo } from './nevigationService';
import { getAdminConsoleToken } from './apiService';
import { validateToken, clearAuthData, getStoredToken } from '../utils/authUtils';

// Flag to prevent multiple simultaneous auth cleanup/navigation attempts
let isHandlingAuthFailure = false;

// Custom error class for auth failures - allows consuming code to identify and handle silently
class AuthAbortError extends Error {
  isAuthAbort = true;
  constructor(message: string) {
    super(message);
    this.name = 'AuthAbortError';
  }
}

/**
 * Handles authentication failure (expired/missing token)
 * Ensures cleanup and navigation only happen once even with concurrent requests
 * Returns a rejected promise with AuthAbortError to properly abort the request
 */
const handleAuthFailure = (errorMessage: string): Promise<never> => {
  if (!isHandlingAuthFailure) {
    isHandlingAuthFailure = true;
    clearAuthData();
    
    // Log for debugging purposes (won't show error overlay)
    console.warn('Auth failure:', errorMessage);
    
    // Try to navigate, but fallback to window.location if navigation service isn't ready
    try {
      navigateTo('/login', {state: {from: window.location.pathname}});
    } catch (e) {
      console.error(e);
      
      // Fallback to direct navigation if React Router isn't ready yet
      window.location.href = '/login';
    }
    
    // Reset flag after navigation to allow future auth failures to be handled
    setTimeout(() => {
      isHandlingAuthFailure = false;
    }, 1000);
  }
  
  // Return a rejected promise with a custom error that can be identified and handled silently
  return Promise.reject(new AuthAbortError(errorMessage));
};

// Helper to check if an error is an auth abort (for use in catch blocks)
export const isAuthAbortError = (error: unknown): boolean => {
  return error instanceof AuthAbortError || 
    (error !== null && typeof error === 'object' && 'isAuthAbort' in error);
};

// Create a base Axios instance
const axiosInstance = axios.create({
  // baseURL: process.env.REACT_APP_API_URL,
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 300000, // Default timeout
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'No Auth',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    const language = localStorage.getItem('language');
    const clinicId = localStorage.getItem('clinicId');
  
    config.baseURL = process.env.REACT_APP_API_URL;

    // Skip auth for login/public endpoints
    const publicEndpoints = ['login/', 'token/', 'refresh/', 'register/', 'forgot-password/'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => 
      config.url?.includes(endpoint) || config.url?.includes('/' + endpoint)
    );

    if (!isPublicEndpoint) {
      if (token) {
        // Validate token before using it
        const { isValid } = validateToken(token);
        
        if (isValid) {
          config.headers['Authorization'] = `Bearer ${token}`;
        } else {
          // Token is invalid/expired
          return handleAuthFailure('Token expired');
        }
      } else {
        // No token found
        return handleAuthFailure('No authentication token');
      }
    }
    
    if (language) {
      config.headers['Accept-Language'] = language;
    }

    // Add clinic ID to headers if available
    if (clinicId) {
      config.headers['X-Clinic-Id'] = clinicId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check if this is a 401 error and not from a public endpoint
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url || '';
      const publicEndpoints = ['login/', '/login/', 'token/', 'refresh/', 'register/', 'forgot-password/'];
      const isPublicEndpoint = publicEndpoints.some(endpoint => url.includes(endpoint));
      
      // Only trigger auth failure for protected endpoints
      if (!isPublicEndpoint && !isHandlingAuthFailure) {
        handleAuthFailure('Authentication failed');
      }
    }
    return Promise.reject(error);
  }
);

// Example of how to change the tenant URL
export function setTenantUrl(newUrl: string) {
  localStorage.setItem('tenantBaseUrl', newUrl);
}

function ensureTrailingSlash(url: string): string {
  // Revert to pass-through: do not modify URLs globally
  return url;
}



const apiCache: { [key: string]: { timestamp: number; promise: Promise<unknown> } } = {};
let droppedDuplicateApiCallCount = 0;

// Cache cleanup mechanism
function cleanupApiCache(ttl: number = 2000) {
  const now = Date.now();
  Object.keys(apiCache).forEach((key) => {
    if (now - apiCache[key].timestamp > ttl) {
      delete apiCache[key];
    }
  });
}

// Optional: expose a manual cache clear function
export function clearApiCache() {
  Object.keys(apiCache).forEach((key) => delete apiCache[key]);
}

export function getDroppedDuplicateApiCallCount(): number {
  return droppedDuplicateApiCallCount;
}

function getCachedPromise<T>(key: string, ttl: number = 1000): Promise<T> | undefined {
  cleanupApiCache(ttl); // Clean up expired cache entries before checking
  const now = Date.now();
  if (apiCache[key] && now - apiCache[key].timestamp < ttl) {
    droppedDuplicateApiCallCount++;
    return apiCache[key].promise as Promise<T>;
  }
  return undefined;
}

// Generic GET method

export const getRequest = async <T>(
  url: string,
  config?: AxiosRequestConfig,
  responseType: AxiosRequestConfig['responseType'] = 'json'
): Promise<T> => {
  try {
    const key = 'GET:' + url + ':' + JSON.stringify(config);
    const cachedPromise = getCachedPromise<T>(key);
    if (cachedPromise) {
      return cachedPromise;
    }
    const axiosConfig = {
      ...config,
      responseType,
      ...(config?.signal ? { signal: config.signal } : {})
    };
    const promise = axiosInstance.get<T>(ensureTrailingSlash(url), axiosConfig)
      .then((response) => response.data);
    apiCache[key] = { timestamp: Date.now(), promise };
    return promise;
  } catch (error) {
    handleError(error);
    throw error;
  }
};

// Generic POST method
export const postRequest = async <T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
  responseType: AxiosRequestConfig['responseType'] = 'json'
): Promise<T> => {
  try {
    const key = 'POST:' + url + ':' + JSON.stringify(data) + ':' + JSON.stringify(config);
    const cachedPromise = getCachedPromise<T>(key);
    if (cachedPromise) {
      return cachedPromise;
    }
    const promise = axiosInstance.post<T>(ensureTrailingSlash(url), data, {
      ...config,
      headers: {
        ...(config?.headers ?? {}),
      },
      responseType,
    }).then((response) => response.data);
    apiCache[key] = { timestamp: Date.now(), promise };
    return promise;
  } catch (error) {
    handleError(error);
    throw error;
  }
};

// Generic PUT method
export const putRequest = async <T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
  responseType: AxiosRequestConfig['responseType'] = 'json'
): Promise<T> => {
  try {
    const key = 'PUT:' + url + ':' + JSON.stringify(data) + ':' + JSON.stringify(config);
    const cachedPromise = getCachedPromise<T>(key);
    if (cachedPromise) {
      return cachedPromise;
    }
    const promise = axiosInstance.put<T>(ensureTrailingSlash(url), JSON.stringify(data), {
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
      ...config,
      responseType,
    }).then((response) => response.data);
    apiCache[key] = { timestamp: Date.now(), promise };
    return promise;
  } catch (error) {
    handleError(error);
    throw error;
  }
};

// Generic DELETE method
export const deleteRequest = async <T>(
  url: string,
  config?: AxiosRequestConfig,
  responseType: AxiosRequestConfig['responseType'] = 'json'
): Promise<T> => {
  try {
    const key = 'DELETE:' + url + ':' + JSON.stringify(config);
    const cachedPromise = getCachedPromise<T>(key);
    if (cachedPromise) {
      return cachedPromise;
    }
    const promise = axiosInstance.delete<T>(ensureTrailingSlash(url), {
      ...config,
      responseType,
    }).then((response) => response.data);
    apiCache[key] = { timestamp: Date.now(), promise };
    return promise;
  } catch (error) {
    handleError(error);
    throw error;
  }
};

// Generic PATCH method
export const patchRequest = async <T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig,
  responseType: AxiosRequestConfig['responseType'] = 'json'
): Promise<T> => {
  try {
    const key = 'PATCH:' + url + ':' + JSON.stringify(data) + ':' + JSON.stringify(config);
    const cachedPromise = getCachedPromise<T>(key);
    if (cachedPromise) {
      return cachedPromise;
    }
    const promise = axiosInstance.patch<T>(ensureTrailingSlash(url), data, {
      ...config,
      headers: {
        ...(config?.headers ?? {}),
      },
      responseType,
    }).then((response) => response.data);
    apiCache[key] = { timestamp: Date.now(), promise };
    return promise;
  } catch (error) {
    handleError(error);
    throw error;
  }
};

// Error handling function
const handleError = (error: unknown): void => {
  // Silently ignore auth abort errors - user is being redirected to login
  if (isAuthAbortError(error)) {
    return;
  }
  
  if (axios.isAxiosError(error)) {
    // Don't redirect to login here - the response interceptor handles that
    if (error.response?.status === 401) {
      console.warn('401 Unauthorized');
      return;
    }
    if (error.response?.status === 403) {
      // Suppress error overlay; show a friendly message (optional)
      // You can use a notification system or set an error state in your component
      // Example: addAlert('You do not have permission to perform this action.', 'warning');
      console.warn('403 Forbidden: You do not have permission to perform this action.');
      return;
    }
    const validationErrors = error.response?.data.errors;
    if (Array.isArray(validationErrors) && validationErrors.length > 0) {
      console.error('Validation error detail:', validationErrors[0].detail);
      // addAlert(validationErrors[0].detail, 'warning');
    } else {
      console.error('Axios error:', error.response?.data);
      // addAlert('An error occurred while processing your request.', 'error');
    }
  } else {
    console.error('General error:', error);
    // addAlert('An unexpected error occurred.', 'error');
  }
};



export async function uploadFileToS3(file: File, presignedUrl: string): Promise<void> {
  try {
    const response = await axios.put(presignedUrl, file, {
      headers: {
        'Content-Type': file.type, // Setting the content type of the file
      },
    });

    // eslint-disable-next-line no-empty
    if (response.status === 200) {

    } else {
      console.error('File upload failed:', response);
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Axios error during file upload:', error.response?.data);
    } else {
      console.error('Unknown error during file upload:', error);
    }
  }
}

export const redirectToAdminConsole = async (
): Promise<void> => {
  const data = getAdminConsoleToken();
  Promise.resolve(data).then((response) => {
    const adminConsoleUrl = response.url;
    const token = response.redirect_token;
    window.open(adminConsoleUrl + '?redirect_token=' + token, '_blank');
  }).catch((error) => {
    console.error(error);
  });
};